import {UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {DatePipe} from '@angular/common';
import {Observable, combineLatest} from 'rxjs';
import {map, shareReplay} from 'rxjs/operators';
import {v4} from 'uuid';

import isEqual from 'fast-deep-equal';
import {FileExtension} from './components/file-upload/file-upload.component';
import HoldingType from './enums/HoldingType.enum';
import {StorageObjectReqRes} from './models/StorageObjectReqRes.model';
import {MediaType} from '../models/metaFileLink.model';
import {InvestorContactReqRes} from '../dashboard/contacts/models/investorContactReqRes.model';
import {UnitBankAccountReqRes} from './models/gp/UnitBankAccountReqRes.model';
import {MergeUnitClientBankAccountDto} from './models/MergeUnitClientBankAccountDto.model';
import MergeUnitClientBankAccountType from './enums/MergeUnitClientBankAccountType.enum';
import {UnitFullName} from './models/unit-bank-account/UnitFullName.model';
import {UnitAddress} from './models/unit-bank-account/UnitAddress.model';
import {CountryModel} from './models/CountryModel';
import {LocationDetailsResponse} from './models/LocationDetailsResponse.model';
import {AutoComplete} from 'primeng/autocomplete';
import moment from 'moment';

export class TerraUtils {
  public static readonly consts = {
    averageClientAge: moment().subtract(40, 'year').toDate(),
    dueDateMinNumOfDays: 7,
    countryCode: {
      IL: 'IL',
      GB: 'GB',
      US: 'US',
      CA: 'CA',
      AU: 'AU',
      ZA: 'ZA',
      IN: 'IN',
      BE: 'BE',
      FR: 'FR',
      DE: 'DE',
      IT: 'IT',
      NL: 'NL',
      RO: 'RO',
      ES: 'ES',
      CH: 'CH',
      AT: 'AT',
      CY: 'CY',
      BG: 'BG',
      HR: 'HR',
      DK: 'DK',
      EE: 'EE',
      FI: 'FI',
      GR: 'GR',
      HU: 'HU',
      IS: 'IS',
      IE: 'IE',
      LV: 'LV',
      LI: 'LI',
      LT: 'LT',
      LU: 'LU',
      MT: 'MT',
      MC: 'MC',
      NO: 'NO',
      PL: 'PL',
      PT: 'PT',
      SM: 'SM',
      SK: 'SK',
      SI: 'SI',
      SE: 'SE',
      CZ: 'CZ',
      TR: 'TR',
      GE: 'GE',
      AE: 'AE',
      UA: 'UA',
    },
    months: [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ],
    paths: {
      ROOT: '/',
      DASHBOARD: '/dashboard',
      ONBOARDING: '/onboarding',
      LOGOUT: '/logout',
      MAINTENANCE: '/maintenance',
      FORGOT_PASSWORD: '/forgot-password',
      ASSETS: '/dashboard/assets',
      FUNDS: '/dashboard/funds',
      TWO_FACTOR_VERIFICATION: '/two-factor',
      EMAIL_VERIFICATION: '/email-verification',
      NOT_FOUND: '/not-found',
      SUPPORT: '/contact-us',
      EMAIL_CONFIRM: '/email-confirm',
      MANAGE_USERS: '/account/manage-users',
    },
    messages: {
      GENERAL_SUBMIT_ERROR:
        'An error occurred while trying to submit the information. Please contact our support team.',
      GENERAL_SUBMIT_ERROR_WITH_LINK:
        'An error has occurred. Please contact <a target="_blank" class="light link" mat-button href="//www.covercy.com/contact-us">support</a>.',
      GENERAL_FRONTEND_ERROR:
        'Please review and correct the fields marked in red',
      GENERAL_RESOURCE_NOT_ALLOWED:
        'You don\'t have permission to access this resource',
    },
    validators: {
      MEMO_MAX_LENGTH: 40,
      PHONE_MAX_LENGTH: 35,
      CITY_LENGTH: 100,
      STREET_LENGTH: 200,
      HOUSE_NUMBER_LENGTH: 200,
      ZIP_LENGTH: 10,
      STATE_LENGTH: 50,
      FIRSTNAME_LENGTH: 40,
      LASTNAME_LENGTH: 40,
      SHORT_TEXT_LENGTH: 300,
      GENERAL_TEXT_LENGTH: 4000,
      GENERAL_LONG_STRING_MAX_LENGTH: 10000,
      POSTAL_CODE_PATTERN: '^[0-9]{5}(?:-[0-9]{4})?$',
      // This array is also reflected in back end. Make sure to update the back end for each update here
      // Custom array that inherits Array prototype methods but can display special characters when joined
      FORBIDDEN_CHARACTERS_IN_NAME: (() => {
        const chars = Object.create(Array.prototype);
        const originalArray = [
          '/',
          '\\',
          '{',
          '}',
          '=',
          '*',
          '$',
          '?',
          '>',
          '<',
          '\t'
        ];
        // Copy the array elements
        originalArray.forEach((char, i) => {
          chars[i] = char;
        });
        chars.length = originalArray.length;
        // Override only the join method
        Object.defineProperty(chars, 'join', {
          value: function (separator: string) {
            return Array.prototype.map.call(this,
              char => char === '\t' ? 'TAB' : char
            ).join(separator);
          }
        });

        return chars;
      })()
    },
    DISPLAY_TABLE_PER_PAGE: 25,
    EXTERNAL_SCRIPTS: {
      // HELLO_SIGN: 'https://s3.amazonaws.com/cdn.hellosign.com/public/js/hellosign-embedded.LATEST.min.js',
      ConfettiParticle: '/assets/ConfettiParticle.js',
    },
    supportedFileExtensions: {
      LOGO: ['.jpeg', '.jpg', '.png', '.gif'] as FileExtension[],
      CONTACTS_IMPORT: ['.xlsx'] as FileExtension[],
      CREATE_DISTRIBUTION_IMPORT: ['.xlsx'] as FileExtension[],
      CREATE_CONTRIBUTION_IMPORT: ['.xlsx'] as FileExtension[],
      BANK_ACCOUNT_IMPORT: ['.xlsx'] as FileExtension[],
    },
    GpPageTitleSuffix: ' | Covercy GP',
    LpPageTitleSuffix: ' | Covercy Investor Portal',
    DATE_ONLY: `MMM dd, yyyy`,
    LONG_DATE_AND_TIME: `MMM dd, yyyy 'at' HH:mm a`,
    APP_DATE_FORMATS: {
      parse: {dateInput: {month: 'short', year: 'numeric', day: 'numeric'}},
      display: {
        dateInput: {month: 'short', year: 'numeric', day: 'numeric'},
        monthYearLabel: {year: 'numeric'},
      },
    },
    EXAMPLE_ASSET_ID: -999,
    // Data points available to collect per AssetType

    /** For each holding datapoint, a list of the holding types that allow this datapoint to be filled. */
    allowedDataPointsListFor: {
      lotSizeArea: [
        HoldingType.SingleFamily,
        HoldingType.MultiFamily,
        HoldingType.Condominium,
        HoldingType.Office,
        HoldingType.Industrial,
        HoldingType.Residential,
        HoldingType.Retail,
        HoldingType.Development,
        HoldingType.Hotel,
        HoldingType.SelfStorage,
        HoldingType.Other,
      ],
      stories: [
        HoldingType.SingleFamily,
        HoldingType.MultiFamily,
        HoldingType.Condominium,
        HoldingType.Office,
        HoldingType.Industrial,
        HoldingType.Retail,
        HoldingType.Development,
        HoldingType.Hotel,
      ],
      residentialUnits: [
        HoldingType.MultiFamily,
        HoldingType.Residential,
        HoldingType.Condominium,
        HoldingType.Development,
      ],
      retailUnits: [
        HoldingType.MultiFamily,
        HoldingType.Condominium,
        HoldingType.Office,
        HoldingType.Retail,
        HoldingType.Development,
        HoldingType.Hotel,
      ],
      rooms: [HoldingType.Hotel],
      grossBuildingArea: [
        HoldingType.MultiFamily,
        HoldingType.Condominium,
        HoldingType.Office,
        HoldingType.Industrial,
        HoldingType.Retail,
        HoldingType.Development,
        HoldingType.Hotel,
      ],
      residentialNetSellableArea: [
        HoldingType.SingleFamily,
        HoldingType.MultiFamily,
        HoldingType.Condominium,
        HoldingType.Development,
      ],
      retailNetSellableArea: [
        HoldingType.MultiFamily,
        HoldingType.Condominium,
        HoldingType.Office,
        HoldingType.Retail,
        HoldingType.Development,
        HoldingType.Hotel,
      ],
      numberOfSites: [
        HoldingType.ManufacturedHomeCommunity,
        HoldingType.RVPark,
      ],
    },
    BULK_OFFERING_DECK_QUEUE_THRESHOLD: 100,
    // supportedFileExtensionsForPreview: ['avi', 'mpeg', ''],
    OAUTH_LOCAL_STORAGE_KEY: 'oauth',
    PLAID_OAUTH_REDIRECT_QUERY_PARAM_NAME: 'oauth_state_id',
  };

  /**
   * Convert month number (1 base e.g January is month number 1) to month name
   * @param monthNumber month number
   */
  public static monthNumberToMonthName(monthNumber: number): string {
    if (monthNumber > this.consts.months.length) {
      return '';
    }

    return this.consts.months[monthNumber - 1];
  }

  public static formattedAmountValidator(control: UntypedFormControl) {
    // a pattern for any thousand separeted number, allowing decimal part
    const pattern = /^\d{1,3}(,\d{3})*(\.\d+)?$/;

    const value = control.value;
    // if invalid
    if (!pattern.test(value)) {
      return {
        valid: false,
      };
    }

    return null;
  }

  public static groupBy<T>(arr: T[], key: string): { [key: string]: T[] } {
    return arr.reduce((res, current) => ({...res, [current[key]]: [...(res[current[key]] || []), current]}), {});
  }

  public static scrollToTop() {
    window.scroll(0, 0);
  }

  public static scrollToElement(el: HTMLElement, isSmooth = false) {
    const options = {
      behavior: isSmooth ? 'smooth' : 'auto',
    } as ScrollIntoViewOptions;
    el.scrollIntoView(options);
  }

  public static scrollToElementById(elementId: string, isSmooth = false) {
    const el: HTMLElement = document.getElementById(elementId);
    if (el) {
      const options = {
        behavior: isSmooth ? 'smooth' : 'auto',
      } as ScrollIntoViewOptions;
      el.scrollIntoView(options);
    }
  }

  /**
   * Getting local date and return same date as UTC (not converting, just forcing it to be considered as UTC)
   * @param nonUtCDate
   * @returns
   */
  public static forceUtc(nonUtCDate: Date): Date {
    if (!nonUtCDate) {
      return null;
    }
    const dateToForce =
      typeof nonUtCDate === 'string' ? nonUtCDate : nonUtCDate.toDateString();
    return moment.utc(dateToForce, ['ddd MMM DD YYYY', 'Y-MM-DD HH:mm:ss.SSS Z']).toDate();
  }

  /**
   * Getting local date and specific hours and return same data with hours as UTC (not converting, just forcing it to be considered as UTC)
   * @param nonUtCDate
   * @param hours
   * @param min
   * @param sec
   * @returns
   */
  public static forceUtcWithHours(
    nonUtCDate: Date,
    hours: number = 23,
    min: number = 59,
    sec: number = 59
  ): Date {
    if (!nonUtCDate) {
      return null;
    }

    const dateToForce =
      typeof nonUtCDate === 'string' ? nonUtCDate : nonUtCDate.toDateString();
    return moment.utc(`${dateToForce} ${hours}:${min}:${sec}`).toDate();
  }

  /**
   * Getting UTC date and return same date as local date (not converting, just forcing it to be considered as local)
   * @param utCDate
   * @returns
   */
  public static forceUtcToLocal(utCDate: Date): Date {
    if (!utCDate) {
      return null;
    }
    return new Date(
      moment.utc(utCDate).format('YYYY-MM-DDTHH:mm:ss')
    );
  }

  public static addCommas(numberWithoutCommas: string): string {
    if (numberWithoutCommas !== null) {
      numberWithoutCommas = numberWithoutCommas.toString();
      // We want to add commas only to the integer part (left of the decimal point)
      const intDec = numberWithoutCommas.split('.');
      let formattedInteger = intDec[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      if (intDec.length === 2) {
        formattedInteger += '.' + intDec[1];
      }
      return formattedInteger;
    }
    return null;
  }

  // Remove all commas from a string (comma separated number string)
  public static removeCommas(numberWithCommas: string): string {
    if (numberWithCommas !== null) {
      return numberWithCommas.split(',').join('');
    }
    return null;
  }

  public static replaceText(text, keyValues) {
    if (!text) {
      return '';
    }
    Object.entries(keyValues).forEach(([key, value]) => {
      const rgx = new RegExp('{' + key + '}', '');

      text = text.replace(rgx, keyValues[key]);
    });
    return text;
  }

  public static setErrorsOnAllFormControls(formGroup: UntypedFormGroup) {
    Object.keys(formGroup.controls).forEach((field) => {
      const control = formGroup.get(field);
      control.markAsTouched({onlySelf: false});
    });
  }

  public static levenshtein_distance(s: string, t: string) {
    if (!s.length) {
      return t.length;
    }
    if (!t.length) {
      return s.length;
    }

    return Math.min(
      this.levenshtein_distance(s.substr(1), t) + 1,
      this.levenshtein_distance(t.substr(1), s) + 1,
      this.levenshtein_distance(s.substr(1), t.substr(1)) +
      (s[0] !== t[0] ? 1 : 0)
    );
  }

  public static makeid(length = 10) {
    let result = '';
    const characters =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const charactersLength = characters.length;
    let counter = 0;
    while (counter < length) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
      counter += 1;
    }
    return result;
  }

  public static uuidv4(): string {
    return v4();
  }

  public static appendExternalScript(url: string) {
    return new Promise((resolve, reject) => {
      const script: any = document.createElement('script');
      script.type = 'text/javascript';
      script.src = url;
      if (script.readyState) {
        // IE
        script.onreadystatechange = () => {
          if (
            script.readyState === 'loaded' ||
            script.readyState === 'complete'
          ) {
            script.onreadystatechange = null;
            resolve({script: name, loaded: true, status: 'Loaded'});
          }
        };
      } else {
        // Others
        script.onload = () => {
          resolve({script: name, loaded: true, status: 'Loaded'});
        };
      }
      script.onerror = (error: any) =>
        resolve({script: name, loaded: false, status: 'Loaded'});
      document.getElementsByTagName('head')[0].appendChild(script);
    });
  }

  public static arraysEqual(_arr1, _arr2, sortBeforeCheck = false) {
    if (
      !Array.isArray(_arr1) ||
      !Array.isArray(_arr2) ||
      _arr1.length !== _arr2.length
    ) {
      return false;
    }
    let arr1 = _arr1.concat();
    let arr2 = _arr2.concat();
    if (sortBeforeCheck) {
      arr1 = arr1.sort();
      arr2 = arr2.sort();
    }
    for (let i = 0; i < arr1.length; i++) {
      if (arr1[i] !== arr2[i]) {
        return false;
      }
    }
    return true;
  }

  public static isEmpty(obj) {
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        return false;
      }
    }
    return true;
  }

  public static isSameYear(periodStart: Date, periodEnd: Date) {
    return periodStart.getFullYear() === periodEnd.getFullYear();
  }

  /**
   * Get number of days between 2 dates
   */
  public static daysBetween(date1: Date, date2: Date): number {
    if (!date1 || !date2) {
      return 0;
    }

    const utc1 = moment(date1).utc(true).startOf('day');
    const utc2 = moment(date2).utc(true).startOf('day');

    return Math.abs(utc1.diff(utc2, 'days'));
  }

  /**
   * Compare function for sort date with null value
   * @param date1
   * @param date2
   * @returns
   */
  public static compareDateWithNull(date1: Date, date2: Date) {
    if (!date1 && !date2) {
      return 0;
    }
    if (!date1) {
      return 1;
    }
    if (!date2) {
      return -1;
    }
    // const date1Iso = new Date(date1).toISOString();
    // const date2Iso = new Date(date2).toISOString();
    const momentDate1 = moment(date1).startOf('day');
    const momentDate2 = moment(date2).startOf('day');

    if (momentDate1.isBefore(momentDate2)) {
      return 1;
    }
    if (momentDate1.isAfter(momentDate2)) {
      return -1;
    }
    return 0;

    // if(date1Iso === date2Iso){
    //   return 0
    // }
    // return date1Iso > date2Iso ? 1 : -1;
  }

  public static getPeriodText(
    periodStart: Date,
    periodEnd: Date,
    datePipe: DatePipe
  ) {
    if (!periodStart || !periodEnd || !datePipe) {
      return 'N.A';
    }
    periodStart = new Date(periodStart);
    periodEnd = new Date(periodEnd);

    if (this.isSameYear(periodStart, periodEnd)) {
      const periodStartMonth = periodStart.getMonth();

      if (
        periodStartMonth === 0 &&
        periodStart.getDate() === 1 &&
        periodEnd.getMonth() === 11 &&
        periodEnd.getDate() === 31
      ) {
        return `Year ${periodStart.getFullYear()}`;
      }

      // Definitions:
      const qStartMonths = [0, 3, 6, 9];
      const qEndDate = [31, 30, 30, 31];

      if (
        qStartMonths.findIndex((x) => x === periodStartMonth) !== -1 &&
        periodEnd.getMonth() === periodStartMonth + 2
      ) {
        if (periodStart.getDate() === 1) {
          // Check that the end date of the end period is the last datte in the month:
          const quarter =
            qStartMonths.findIndex((x) => x === periodStartMonth) + 1;
          if (periodEnd.getDate() === qEndDate[quarter - 1]) {
            return `Q${quarter} ${periodStart.getFullYear()}`;
          }
        }
      }
    }
    return `${datePipe.transform(periodStart)} - ${datePipe.transform(
      periodEnd
    )}`;
  }

  // If the provided date boundaries exactly match a year quarter, retur its number
  public static getQuarterNumber(periodStart: Date, periodEnd: Date): number {
    periodStart = new Date(periodStart);
    periodEnd = new Date(periodEnd);

    if (!this.isSameYear(periodStart, periodEnd)) {
      return null;
    }

    const periodStartMonth = periodStart.getMonth();

    // Definitions:
    const qStartMonths = [0, 3, 6, 9];
    const qEndDate = [31, 30, 30, 31];

    if (
      qStartMonths.findIndex((x) => x === periodStartMonth) !== -1 &&
      periodEnd.getMonth() === periodStartMonth + 2
    ) {
      if (periodStart.getDate() === 1) {
        // Check that the end date of the end period is the last datte in the month:
        const quarter =
          qStartMonths.findIndex((x) => x === periodStartMonth) + 1;
        if (periodEnd.getDate() === qEndDate[quarter - 1]) {
          return quarter;
        }
      }
    }
    return null;
  }

  public static getCurrentQuarterNumber(): 1 | 2 | 3 | 4 {
    const current = new Date();
    const year = current.getFullYear();

    if (current >= new Date(year, 9, 1, 12)) {
      return 4;
    } else if (current >= new Date(year, 6, 1, 12)) {
      return 3;
    } else if (current >= new Date(year, 3, 1, 12)) {
      return 2;
    }
    return 1;
  }

  public static getCurrentQuarterBoundaries(): [Date, Date] {
    // find in which quarter we are now, and return its start and end
    const currentQuarter = this.getCurrentQuarterNumber();
    return this.getStartAndEndDates(
      new Date().getFullYear(),
      currentQuarter,
      false
    );
  }

  public static getPreviousQuarterBoundaries(): [Date, Date] {
    const currentQuarter = this.getCurrentQuarterNumber();
    const previousQuarter = currentQuarter === 1 ? 4 : currentQuarter - 1;
    const year =
      previousQuarter === 4
        ? new Date().getFullYear() - 1
        : new Date().getFullYear();
    return this.getStartAndEndDates(year, previousQuarter, false);
  }

  public static getStartAndEndDates(
    year: number,
    quarter: number,
    setHoursTo12 = true
  ): [Date, Date] {
    const response: [Date, Date] = [null, null];
    switch (quarter) {
      case 1:
        response[0] = new Date(year, 0, 1, setHoursTo12 ? 12 : null);
        response[1] = new Date(year, 2, 31, setHoursTo12 ? 12 : null);
        break;
      case 2:
        response[0] = new Date(year, 3, 1, setHoursTo12 ? 12 : null);
        response[1] = new Date(year, 5, 30, setHoursTo12 ? 12 : null);
        break;
      case 3:
        response[0] = new Date(year, 6, 1, setHoursTo12 ? 12 : null);
        response[1] = new Date(year, 8, 30, setHoursTo12 ? 12 : null);
        break;
      case 4:
        response[0] = new Date(year, 9, 1, setHoursTo12 ? 12 : null);
        response[1] = new Date(year, 11, 31, setHoursTo12 ? 12 : null);
        break;
    }
    return response;
  }

  public static getYearsList(
    currentYear: number,
    length: number,
    shouldAddNextYear: boolean
  ): number[] {
    return shouldAddNextYear
      ? Array.from({length}, (x, i) => {
        return currentYear + 1 - i;
      })
      : Array.from({length}, (x, i) => {
        return currentYear - i;
      });
  }

  public static isMobileDevice() {
    return (
      (window.screen &&
        window.screen.availWidth &&
        window.screen.availWidth <= 650) ||
      navigator.userAgent.indexOf('IEMobile') !== -1
    );
  }

  public static isAudioOrVideoSupportedForPreview(
    file: StorageObjectReqRes,
    mediaType: MediaType
  ): boolean {
    let element: HTMLMediaElement;
    switch (mediaType) {
      case MediaType.Video:
        element = document.createElement('video');
        break;
      case MediaType.Audio:
        element = document.createElement('audio');
        break;
      default:
        return false;
    }
    const result = element.canPlayType(file.metaFileLink.mediaType);
    return result === 'maybe' || result === 'probably';
  }

  /** Deep dirty check https://netbasal.com/detect-unsaved-changes-in-angular-forms-75fd8f5f1fa6 */
  public static dirtyCheck<U>(source: Observable<U>) {
    // tslint:disable-next-line: only-arrow-functions
    return function <T>(valueChanges: Observable<T>): Observable<boolean> {
      const isDirty$ = combineLatest([source, valueChanges]).pipe(
        map(([a, b]) => {
          return isEqual(a, b) === false;
        }),
        shareReplay({bufferSize: 1, refCount: true})
      );

      return isDirty$;
    };
  }

  /**
   * Marks all controls in a form group as touched
   * @param formGroup - The form group to touch
   */
  public static validateAllFormFields(formGroup: UntypedFormGroup) {
    // tslint:disable-next-line: no-angle-bracket-type-assertion
    (<any> Object).values(formGroup.controls).forEach((control) => {
      control.markAsTouched();
      control.updateValueAndValidity();

      if (control.controls) {
        this.validateAllFormFields(control);
      }
    });
  }

  private static buildRegexForTestWordsAnyOrder(
    keywordsString: string,
    startWithBeginningOfWords = false
  ): RegExp {
    // https://stackoverflow.com/a/41869757/4308457
    if (!startWithBeginningOfWords) {
      return new RegExp(
        `(?=.*?` +
        keywordsString
          .replace(/[.?*=]/g, ' ')
          .split(' ')
          .filter((i) => i !== '')
          .join(`)(?=.*?`) +
        `).*`,
        `i`
      );
    } else {
      return new RegExp(
        `(?=.*?\\b` +
        keywordsString
          .replace(/[.?*=]/g, ' ')
          .split(' ')
          .filter((i) => i !== '')
          .join(`)(?=.*?\\b`) +
        `).*`,
        `i`
      );
    }
  }

  /** Search for a term in a string of keywords. Will return true if the term matches the keywords in any order.
   * Case insensitive.
   */
  public static searchKeywordsInAnyOrder(
    searchTerm: string = '',
    keywordsString: string = '',
    startWithBeginningOfWords = false
  ) {
    // https://stackoverflow.com/a/41869757/4308457
    const isFound = this.buildRegexForTestWordsAnyOrder(
      searchTerm,
      startWithBeginningOfWords
    ).test(keywordsString);
    return isFound;
  }

  /** Returns the full name of a contact (first, middle and last name)  */
  public static getContactFullName(contact: InvestorContactReqRes) {
    if (!contact) {
      return '';
    }
    let fullName = `${contact.firstName || ''} ${contact.middleName || ''} ${contact.lastName || ''
    }`.trim();
    if (!contact.middleName) {
      fullName = fullName.replace('  ', ' ');
    }
    return fullName;
  }

  public static randomInteger(max): number {
    return Math.floor(Math.random() * (max + 1));
  }

  public static randomRgbColor(): [number, number, number] {
    let r = this.randomInteger(255);
    let g = this.randomInteger(255);
    let b = this.randomInteger(255);
    return [r, g, b];
  }

  public static randomHexColor(): string {
    let [r, g, b] = this.randomRgbColor();
    let hr = r.toString(16).padStart(2, '0');
    let hg = g.toString(16).padStart(2, '0');
    let hb = b.toString(16).padStart(2, '0');
    return '#' + hr + hg + hb;
  }

  public static convertUnitBankAccountToMergeUnitClient(
    ba: UnitBankAccountReqRes
  ) {
    const bankAccount: MergeUnitClientBankAccountDto = {
      id: ba.id,
      accountType: MergeUnitClientBankAccountType.CovercyAccount,
      accountNumber: ba.accountNumber,
      accountNickname: ba.accountNickname,
      iso: 'USD',
      preferredCurrency: {...ba.preferredCurrency},
      unitBalance: ba.balance,
      UnitAvailableBalance: ba.available,
    } as MergeUnitClientBankAccountDto;
    return bankAccount;
  }

  public static getUnitFullName(unitFullName: UnitFullName): string {
    if (!unitFullName) {
      return '';
    }
    return `${unitFullName.first} ${unitFullName.last}`;
  }

  public static populateUnitAddress(
    addressForm: any,
    allStates: Array<{ abbreviation: string; name: string }>,
    allCountries: CountryModel[]
  ): UnitAddress {
    const address = new UnitAddress();
    if (addressForm) {
      address.street = addressForm.streetName;
      address.street2 = addressForm.street2Name;
      address.city = addressForm.cityName;
      address.state = addressForm.stateName
        ? allStates.find((s) => s.name === addressForm.stateName)?.abbreviation
        : null;
      address.postalCode = addressForm.postalCode;
      address.country = addressForm.countryId
        ? allCountries.find((c) => c.id === addressForm.countryId)?.code
        : null;
    }
    return address;
  }

  public static unitAddressToLocationObj(
    unitAddress: UnitAddress,
    allStates: Array<{ abbreviation: string; name: string }>,
    allCountries: CountryModel[]
  ): LocationDetailsResponse {
    const address = new LocationDetailsResponse();
    if (!!unitAddress) {
      address.streetName = unitAddress.street;
      address.street2Name = unitAddress.street2;
      address.cityName = unitAddress.city;
      address.stateName = unitAddress.state
        ? allStates.find((s) => s.abbreviation === unitAddress.state)?.name
        : null;
      address.postalCode = unitAddress.postalCode;
      address.countryId = unitAddress.country
        ? allCountries.find((c) => c.code === unitAddress.country)?.id
        : null;
    }
    return address;
  }
}

export function isNullOrUndefined(obj: any) {
  return obj === null || obj === undefined;
}

export function getArrayDifferentById(arr1: any, arr2: any) {
  return arr1
    .map((i1) => i1.id)
    .filter((i3) => !arr2.map((i2) => i2.id).includes(i3));
}

export function getArrayCommonById(arr1: any, arr2: any) {
  return arr1
    .map((i1) => i1.id)
    .filter((i3) => arr2.map((i2) => i2.id).includes(i3));
}

export function getArrayIncludeAllById(arr1: any, arr2: any) {
  return arr1
    .map((i1) => i1.id)
    .every((i3) => arr2.map((i2) => i2.id).includes(i3));
}

export function getArrayIncludeSomeById(arr1: any, arr2: any) {
  return (
    arr1.map((r) => r.id).some((r) => arr2.map((s) => s.id).includes(r)) &&
    !arr1.map((r) => r.id).every((r) => arr2.map((s) => s.id).includes(r))
  );
}

/**
 * Get an array of distinct objects by id property, to be used in array's filter method
 */
export function distinctById(value: any, index: number, self: Array<any>) {
  return self.findIndex((x) => x.id === value.id) === index;
}

AutoComplete.prototype.onInputBlur = function(
  this: AutoComplete,
  event: KeyboardEvent
) {
  try {
    if (this.highlightOption) {
      // this.selectItem(this.highlightOption, false);
      this.cd.detectChanges();
    }
  } catch (e) {
  }

  this.focused = false;
  this.onModelTouched();
  this.onBlur.emit(event);
};
